/* eslint-disable no-nested-ternary */
import { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';

import { MovieOrSeriesOrVideo } from '@rikstv/play-common/src/forces/utils/microdata.type';
import { commonRoutes } from '@rikstv/play-common/src/router/router.path';
import { getLogger } from '@rikstv/play-common/src/utils/logger/logger';
import { addParameters, toAbsolute } from '@rikstv/play-common/src/utils/url/url.utils';

import { ContentStackMetadata, SuperCoverPageData } from '../contentStack.types';
import { getStackContent } from '../hooks/scp.utils';
import { KNOWN_LABELS } from '../hooks/useContentStackEventHandlers';

const logger = getLogger('[microdata]');

export const ScpHead: FC<{ scpData: SuperCoverPageData }> = ({ scpData }) => {
  const data = getStackContent(scpData);
  const { metaProps, microData } = getMetaAndMicroData(scpData);
  usePathnameWithTitle(scpData);

  useEffect(() => logger.log({ metaProps, microData }));

  return (
    <Helmet>
      <title>{data.metadata.title}</title>
      {metaProps.map(metaProp => (
        <meta key={metaProp.property || metaProp.name} {...metaProp} />
      ))}
      <script type="application/ld+json">{JSON.stringify(microData)}</script>
      <link
        rel="canonical"
        href={toAbsolute(
          commonRoutes.stackedSeriesOrMovie({
            id: scpData.id,
            isSeries: scpData.type === 'series',
          })
        )}
      />
    </Helmet>
  );
};

const usePathnameWithTitle = (data: SuperCoverPageData) => {
  const navigate = useNavigate();
  const { pathname, state, search } = useLocation();

  useEffect(() => {
    const isSeries = data.type !== 'singleProgramOrMovie';
    const _data = getStackContent(data);
    if (data) {
      const pathWithTitle = commonRoutes.stackedSeriesOrMovie({
        id: _data.tracking.elementId,
        title: _data.metadata.title,
        isSeries,
      });
      if (pathname !== pathWithTitle) {
        // Keep query params since they might contain GA utm params
        navigate(pathWithTitle + search, { replace: true, state });
      }
    }
  }, [data, pathname, navigate, state, search]);
};

/**
 * Test structured data here
 * https://developers.google.com/search/docs/appearance/structured-data
 */
const getMetaAndMicroData = (scpData: SuperCoverPageData) => {
  // TODO: handle episode page differently?
  const isSeries = scpData.type !== 'singleProgramOrMovie';
  const movieOrSeries = getStackContent(scpData);

  // Extract metadata
  const extendedMetadata = movieOrSeries.metadata.extendedMetadata ?? [];
  const directors = extendedMetadata.find(a => a.label === KNOWN_LABELS.director)?.values;
  const actors = extendedMetadata.find(a => a.label === KNOWN_LABELS.actors)?.values;
  const genres = extendedMetadata.find(a => a.label === KNOWN_LABELS.genre)?.values;
  const releaseDate = getReleaseDate(movieOrSeries);
  const duration = movieOrSeries.metadata.durationInSeconds;
  const imageUrl = movieOrSeries.images.backdropImage?.href ?? '';

  // Only tag as movie when some things are true
  // e.g. director is often set for documentaries without imdb-rating
  const isProbablyMovie = directors != null || movieOrSeries.metadata.imdbRating != null;

  // Assign OG tags
  const ogType = isSeries ? 'video.tv_show' : isProbablyMovie ? 'video.movie' : 'video.other';
  const metaProps = [
    // https://ogp.me/#type_video.tv_show or
    // https://ogp.me/#type_video.movie or
    // https://ogp.me/#type_video.other
    { property: 'og:type', content: ogType },
    { property: 'og:title', content: movieOrSeries.metadata.title },
    { property: 'og:description', content: movieOrSeries.metadata.description },
    { property: 'og:image', content: addParameters(imageUrl, { width: '1200' }) },
    { name: 'description', content: movieOrSeries.metadata.description },
    // Video properties: https://ogp.me/#type_video
    { property: 'video:release_date', content: releaseDate },
    { property: 'video:duration', content: duration?.toString() }, // in seconds
    { name: 'robots', content: scpData.id.includes('linear') && scpData.type !== 'series' ? 'noindex' : undefined },
  ].filter(p => Boolean(p.content));
  // Assign actors and directors from metadata
  directors?.forEach(director => metaProps.push({ property: 'video:director', content: director }));
  actors?.forEach(actor => metaProps.push({ property: 'video:actor', content: actor }));

  const microData: MovieOrSeriesOrVideo = {
    '@context': 'https://schema.org',
    '@type': isSeries ? 'TVSeries' : isProbablyMovie ? 'Movie' : 'VideoObject',
    name: movieOrSeries.metadata.title,
    description: movieOrSeries.metadata.description,
    image: addParameters(imageUrl, { width: '1200' }),
    thumbnailUrl: imageUrl ? addParameters(imageUrl, { width: '300' }) : '',
    datePublished: releaseDate ?? '',
    uploadDate: releaseDate ?? '', // required by google for rich results
    url: commonRoutes.stackedSeriesOrMovie({
      id: scpData.id,
      isSeries,
      title: movieOrSeries.metadata.title,
    }),
  };
  if (directors) {
    microData.director = directors.map(director => ({ '@type': 'Person', name: director }));
  }
  if (actors) {
    microData.actor = actors.map(actor => ({ '@type': 'Person', name: actor }));
  }
  if (genres) {
    microData.genre = genres;
  }
  if (duration) {
    // iso 8601 duration format
    if ('Temporal' in globalThis) {
      // https://tc39.es/proposal-temporal/docs/duration.html
      const temporal = new (globalThis as any).Temporal.Duration.from({ seconds: duration })
        .round({ largestUnit: 'hour' })
        .toString();
      microData.duration = temporal.toString();
    } else {
      microData.duration = `PT${duration}S`;
    }
  }

  return { metaProps, microData };
};

const getReleaseDate = (stack: ContentStackMetadata): string | undefined => {
  if (stack.metadata.productionYear) {
    return stack.metadata.productionYear.toString();
  }
  if (stack.defaultProviderVariant?.state && stack.defaultProviderVariant.linearData) {
    return stack.defaultProviderVariant.linearData.linearStart.split('T')[0];
  }
};
